// src/components/dashboard/CapitalExpenditureBySectorChart.js
import React, { useMemo } from "react";
import { useAnnCostInvBySector } from "../../hooks/useAnnCostInvBySector";
import { StackedBarChart } from "./charts/StackedBarChart";
import { getSectorColor } from "../../config/colorScheme";
import { SECTOR_CONFIG, SECTOR_ORDER } from "../../lib/sectorMeta";

export function CapitalExpenditureBySectorChart({
  scenario,
  sectors,            // optional: array of codes to include; if omitted, we’ll use SECTOR_ORDER
  from = 2025,
  to = 2050,
  unitScale = 0.001,
}) {
  // If caller passes sectors, also push them to the hook so GraphQL filters server-side
  const wantedCodes = sectors ?? SECTOR_ORDER;

  const { labels, bySectorYear, sectors: sectorsFound, loading, error } =
    useAnnCostInvBySector({ scenario, sectors: wantedCodes, from, to });

  // Only keep the seven whitelisted sectors, in the exact order requested.
  // Also drop anything that doesn't have a label in SECTOR_CONFIG.
  const activeCodes = useMemo(() => {
    const present = new Set(sectorsFound);
    return SECTOR_ORDER.filter((code) => present.has(code) && SECTOR_CONFIG[code]);
  }, [sectorsFound]);

  const datasets = useMemo(() => {
    const fallback = ["#2563eb", "#16a34a", "#f59e0b", "#dc2626", "#7c3aed", "#0ea5e9", "#6b7280"];
    return activeCodes.map((code, i) => {
      const { label, slug } = SECTOR_CONFIG[code];
      console.table(getSectorColor);
      const primary = getSectorColor?.(slug, "primary") || fallback[i % fallback.length];
      const light   = getSectorColor?.(slug, "light")   || `${fallback[i % fallback.length]}33`;
      return {
        label,
        data: labels.map((y) => (bySectorYear[code]?.[Number(y)] ?? 0) * unitScale),
        backgroundColor: light,
        borderColor: primary,
        borderWidth: 1,
        stack: "capex",
      };
    });
  }, [activeCodes, labels, bySectorYear, unitScale]);

  if (loading) return <div className="w-full h-96 grid place-items-center">Loading sector investments…</div>;
  if (error)   return <div className="w-full h-96 text-red-600">Error: {error.message}</div>;

  return (
    <StackedBarChart
      title="Total public and private investment needed"
      labels={labels}
      datasets={datasets}
      yMax={180}
      className="w-full h-96 mt-4"
    />
  );
}
